import React, {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';

import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation} from 'react-router';

import {showLoading, updateLoading} from '@/actions/loadingActions';
import {GetRequest, PostRequest} from '@/api/request';
import {getIsAvailableWifi} from '@/api/wifiApi';
import {changeDeviceWifiStatus} from '@/businesses/deviceBusiness';
import {GET_IS_WIFI_CONNECTED, POST_WIFI_ON_OFF} from '@/constants/apis';
import * as APPDATA from '@/constants/pcConstAppData';
import {WIFI_STATUS} from '@/constants/pcConstAppData';
import {getIsAutoRefreshUpdateSW} from '@/selectors/signalRSelector';

const WifiStatusContext = createContext(null);

export function WifiStatusContextProvider({children}) {
  const intervalAvailableWifiRef = useRef(null);
  const dispatch = useDispatch();
  const deviceStore = useSelector((store) => store.deviceStore);
  const isAutoRefreshUpdateSW = useSelector(getIsAutoRefreshUpdateSW);
  const [wifiPower, setWifiPower] = useState(deviceStore.WifiStatus !== WIFI_STATUS.OFF);
  const [wifiStatus, setWifiStatus] = useState(deviceStore.WifiStatus);

  const location = useLocation();

  useEffect(() => {
    if (location.pathname === APPDATA.ERROR_PAGE_ADDRESS.replace('#', '')) {
      _clearWifiStatusInterval();
    }
  }, [location]);

  /**
   * @param {WIFI_STATUS} status
   */
  const _setWifiStatus = useCallback(
    (status) => {
      switch (status) {
        case WIFI_STATUS.OFF:
          setWifiPower(false);
          break;
        default:
          setWifiPower(true);
          break;
      }
      setWifiStatus(status);
      dispatch(changeDeviceWifiStatus(status));
    },
    [dispatch],
  );

  /**
   * @param {WIFI_STATUS} status
   * @returns
   */
  const _validateIsWifiConnected = useCallback(
    (status) =>
      GetRequest(GET_IS_WIFI_CONNECTED)
        .then((response) => {
          if (response.data) {
            const {IsConnected} = response.data;
            if (IsConnected && status !== WIFI_STATUS.CONNECTED) _setWifiStatus(WIFI_STATUS.CONNECTED);
            else if (!IsConnected && status === WIFI_STATUS.CONNECTED) _setWifiStatus(WIFI_STATUS.ON);
          }
        })
        .catch((error) => console.error(error)),
    [_setWifiStatus],
  );

  const _getWifiStatus = useCallback(() => {
    if (
      [APPDATA.HOME_SCREEN_ADDRESS, APPDATA.WIFI_SETTING_ADDRESS].some(
        (e) => e.replace('#', '') === location.pathname,
      ) &&
      !isAutoRefreshUpdateSW
    )
      getIsAvailableWifi(isAutoRefreshUpdateSW).then((response) => {
        if (response.data) {
          if (wifiStatus === WIFI_STATUS.OFF) _setWifiStatus(WIFI_STATUS.ON);
          _validateIsWifiConnected(wifiStatus);
        }
      });
  }, [_setWifiStatus, _validateIsWifiConnected, isAutoRefreshUpdateSW, location.pathname, wifiStatus]);

  const _registerWifiStatusInterval = useCallback(() => {
    _clearWifiStatusInterval();
    intervalAvailableWifiRef.current = setInterval(() => {
      _getWifiStatus();
    }, 5000);
  }, [_getWifiStatus]);

  useEffect(() => {
    _getWifiStatus();
  }, []);

  useEffect(() => {
    _registerWifiStatusInterval();
    return () => {
      _clearWifiStatusInterval();
    };
  }, [_registerWifiStatusInterval, wifiStatus]);

  useEffect(() => {
    if (wifiPower && wifiStatus === WIFI_STATUS.OFF) _setWifiStatus(WIFI_STATUS.ON);
    else if (!wifiPower && wifiStatus !== WIFI_STATUS.OFF) _setWifiStatus(WIFI_STATUS.OFF);
  }, [_setWifiStatus, wifiPower, wifiStatus]);

  useEffect(() => {
    if (wifiStatus === WIFI_STATUS.ON) _validateIsWifiConnected(wifiStatus);
  }, [_validateIsWifiConnected, wifiStatus]);

  const _clearWifiStatusInterval = () => {
    if (intervalAvailableWifiRef.current) {
      clearInterval(intervalAvailableWifiRef.current);
      intervalAvailableWifiRef.current = null;
    }
  };

  /**
   * @param {Boolean} isTurnOn
   */
  const ChangeWifiPower = useCallback((isTurnOn) => {
    dispatch(
      updateLoading({
        showLoading: true,
        loadingText: 'Please wait...',
      }),
    );
    PostRequest(POST_WIFI_ON_OFF, {Enable: isTurnOn})
      .then((res) => {
        setWifiPower(res.data);
        dispatch(showLoading(false));
      })
      .catch(() => {
        console.log('wifi on off api error');
        dispatch(showLoading(false));
      });
  }, []);

  /**
   * @param {WIFI_STATUS} status
   */
  const ChangeWifiStatus = useCallback(
    (status) => {
      const isTurnOn = status !== WIFI_STATUS.OFF;
      if (wifiPower !== isTurnOn) {
        PostRequest(POST_WIFI_ON_OFF, {Enable: isTurnOn});
      }
      _setWifiStatus(status);
    },
    [_setWifiStatus, wifiPower],
  );

  const WifiStatusContextValue = useMemo(
    () => ({
      wifiStatus,
      ChangeWifiPower,
      ChangeWifiStatus,
    }),
    [ChangeWifiPower, ChangeWifiStatus, wifiStatus],
  );

  return <WifiStatusContext.Provider value={WifiStatusContextValue}>{children}</WifiStatusContext.Provider>;
}

WifiStatusContextProvider.propTypes = {
  children: PropTypes.any,
};

WifiStatusContextProvider.defaultProps = {
  children: null,
};

export const useWifiStatusContext = () => useContext(WifiStatusContext);
